import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { getViewAs, setViewAs as setViewAsTarget, clearViewAs } from '../utils/viewAs';

const MOCK_USER_KEY = 'onward_mock_user';
const USE_MOCK_AUTH = process.env.REACT_APP_USE_MOCK_AUTH === 'true';

// Restore X-Mock-User header before first request (dev only).
if (USE_MOCK_AUTH) {
  const stored = typeof window !== 'undefined' ? localStorage.getItem(MOCK_USER_KEY) : null;
  if (stored) axios.defaults.headers.common['X-Mock-User'] = stored;
}

// (axios.defaults.withCredentials + view-as param interceptor are configured
// once at app boot in utils/axiosSetup.js, before any request fires.)

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [viewingAs, setViewingAsState] = useState(getViewAs());
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data.user);
      setAccount(data.account);
      // /me reports the server's view of who we're impersonating (or null).
      // Trust the server response over local sessionStorage so a stale entry
      // for a deleted target doesn't leave us stuck.
      if (data.viewing_as) {
        setViewingAsState(data.viewing_as);
      } else if (getViewAs()) {
        // Local says we're impersonating but server disagreed — clear it.
        clearViewAs();
        setViewingAsState(null);
      }
    } catch (err) {
      setUser(null);
      setAccount(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Sync to view-as changes from any other component (Sidebar, AdminPage).
  useEffect(() => {
    const handler = () => setViewingAsState(getViewAs());
    window.addEventListener('onward:view_as_changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('onward:view_as_changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const handleLogin = useCallback(async () => {
    if (USE_MOCK_AUTH) {
      const input = prompt('Enter your username:');
      const key = input && input.trim().toLowerCase().replace(/\s+/g, '_');
      if (!key) return;
      localStorage.setItem(MOCK_USER_KEY, key);
      axios.defaults.headers.common['X-Mock-User'] = key;
      await refresh();
      return;
    }
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `${API_URL}/api/auth/login?redirect_to=${returnTo}`;
  }, [refresh]);

  const handleLogout = useCallback(async () => {
    let workosLogoutUrl = null;
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/logout`);
      workosLogoutUrl = data?.workos_logout_url || null;
    } catch (err) {
      // ignore — we're clearing local state regardless
    }
    localStorage.removeItem(MOCK_USER_KEY);
    delete axios.defaults.headers.common['X-Mock-User'];
    clearViewAs();
    setUser(null);
    setAccount(null);
    setViewingAsState(null);
    window.location.href = workosLogoutUrl || '/';
  }, []);

  const enterViewAs = useCallback(async (target) => {
    setViewAsTarget(target);
    setViewingAsState(target);
    await refresh();
  }, [refresh]);

  const exitViewAs = useCallback(async () => {
    clearViewAs();
    setViewingAsState(null);
    await refresh();
  }, [refresh]);

  return {
    user,
    account,
    username: user?.name || null,
    isLoggedIn: !!user,
    isActive: account?.status === 'active',
    isPending: account?.status === 'pending',
    signupComplete: account?.signup_complete === true,
    isAdmin: account?.is_admin === true,
    entitlements: account?.entitlements || [],
    hasEntitlement: (name) => (account?.entitlements || []).includes(name),
    viewingAs,
    enterViewAs,
    exitViewAs,
    isLoading,
    handleLogin,
    handleLogout,
    refresh,
  };
};

export default useAuth;
